import type { ActorEvent } from '@lighthouse/core'
import { EVENT_TYPE, type RecordLikeProtocol } from '@lighthouse/core'
import type { FlowLayoutNode, NodeIdWithScope, NodeRenderProps } from '@lighthouse/shared'
import {
    applyCustomStatisticToRecord,
    checkActionEvent,
    NodeRender,
    selectRecordFromPool,
    useActionRunningLoadings,
    useAtomData,
    useFlowLayoutContext
} from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import React, { forwardRef, memo, useCallback, useDeferredValue, useMemo, useRef } from 'react'

import { ContainerLayout } from '../../components/ContainerLayout'
import { CustomViewBlockProvider, useCustomViewBlockContext, useCustomViewStatisticValues, useViewBlockContext } from '../../context'

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
    data: FlowLayoutNode
    recordId: string
    record?: RecordLikeProtocol
    /** 记录索引路径，用于自定义视图下 block 的 anchor id 后缀，如 "0"、"0_1" */
    recordIndexPath?: string
    readonly?: boolean
    title: string
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

export const Item = memo(
    forwardRef<HTMLDivElement, ItemProps>((props, ref) => {
        return props.record ? <ItemWithProvidedRecord {...props} ref={ref} /> : <ItemFromPool {...props} ref={ref} />
    })
)

const ItemFromPool = memo(
    forwardRef<HTMLDivElement, ItemProps>(
        ({ readonly, recordId, recordIndexPath, data, onRecordClick, title, ...rest }, ref) => {
        const innerRef = useRef<HTMLDivElement>(null)
        const { recordPoolAtom, dataSource, blockData, uniqueId } = useViewBlockContext()
        const { id: dsId } = dataSource
        const selector = useMemo(() => selectRecordFromPool(dsId, recordId), [dsId, recordId])
        const poolRecord = useDeferredValue(useAtomData(recordPoolAtom, selector))
        const customStatistic = useCustomViewStatisticValues()
        const record = useMemo(
            () => applyCustomStatisticToRecord(poolRecord, customStatistic?.[recordId]),
            [customStatistic, poolRecord, recordId]
        )

        const customViewContextValue = useCustomViewBlockValue({
            readonly,
            data,
            title,
            recordIndexPath,
            dsId,
            blockData,
            uniqueId,
            record,
            onRecordClick
        })

        return (
            <CustomViewBlockProvider value={customViewContextValue}>
                <NodeRender ref={mergeRefs([ref, innerRef])} data={data} nodeRender={VirtualContainer} {...rest} />
            </CustomViewBlockProvider>
        )
    })
)

const ItemWithProvidedRecord = memo(
    forwardRef<HTMLDivElement, ItemProps>(({ readonly, recordId, record: providedRecord, recordIndexPath, data, onRecordClick, title, ...rest }, ref) => {
        const innerRef = useRef<HTMLDivElement>(null)
        const { dataSource, blockData, uniqueId } = useViewBlockContext()
        const { id: dsId } = dataSource
        const customStatistic = useCustomViewStatisticValues()
        const record = useMemo(
            () => applyCustomStatisticToRecord(providedRecord, customStatistic?.[recordId]),
            [customStatistic, providedRecord, recordId]
        )

        const customViewContextValue = useCustomViewBlockValue({
            readonly,
            data,
            title,
            recordIndexPath,
            dsId,
            blockData,
            uniqueId,
            record,
            onRecordClick
        })

        return (
            <CustomViewBlockProvider value={customViewContextValue}>
                <NodeRender ref={mergeRefs([ref, innerRef])} data={data} nodeRender={VirtualContainer} {...rest} />
            </CustomViewBlockProvider>
        )
    })
)

interface UseCustomViewBlockValuePayload {
    readonly?: boolean
    data: FlowLayoutNode
    title: string
    recordIndexPath?: string
    dsId: string
    blockData: ReturnType<typeof useViewBlockContext>['blockData']
    uniqueId: string
    record?: RecordLikeProtocol
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

function useCustomViewBlockValue({
    readonly,
    data,
    title,
    recordIndexPath,
    dsId,
    blockData,
    uniqueId,
    record,
    onRecordClick
}: UseCustomViewBlockValuePayload) {
    const ancestorCustomView = useCustomViewBlockContext()
    const parentCustomView = ancestorCustomView.viewId ? ancestorCustomView : undefined
    const {
        id,
        config: { eventList }
    } = blockData

    const recordClickEvent = useMemo(() => eventList?.find(item => item.type === EVENT_TYPE.RECORD_CLICK), [eventList])
    const hasRecordAction = !!recordClickEvent

    const { handleActionTriggerWithLoading } = useActionRunningLoadings()

    const handleRecordClick = useCallback(
        (e: React.MouseEvent) => {
            if (!checkActionEvent(e) || !record || !onRecordClick || !recordClickEvent) {
                return
            }
            handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: recordClickEvent,
                trigger: (id, actorEvent) => onRecordClick?.(id, actorEvent, record)
            })
        },
        [handleActionTriggerWithLoading, onRecordClick, record, recordClickEvent, uniqueId]
    )

    return useMemo(
        () => ({
            readonly,
            editable: !data.disabled,
            name: title,
            viewId: id,
            pointer: dsId,
            record,
            parentCustomView,
            recordIndexPath,
            hasRecordAction,
            handleRecordClick
        }),
        [readonly, data.disabled, title, id, dsId, record, parentCustomView, recordIndexPath, hasRecordAction, handleRecordClick]
    )
}

const VirtualContainer = memo((props: NodeRenderProps) => {
    const { node } = props
    const { readonly, hasRecordAction, handleRecordClick, viewId } = useCustomViewBlockContext()

    const { selectedNodes } = useFlowLayoutContext()
    const isViewSelected = useMemo(
        () => {
            if (readonly || !selectedNodes?.length) {
                return false
            }
            return (
                nodeOrChildrenSelected(selectedNodes, {
                    id: viewId,
                    scope: node.scope,
                    type: 'custom',
                    children: [node]
                })
            )
        },
        [node, readonly, selectedNodes, viewId]
    )

    const showMask = !isViewSelected && !readonly /* && node.disabled */

    return (
        <>
            <ContainerLayout
                {...props}
                // 记录点击只应被真实浮层 trigger / opened portal 阻断，隐藏浮层不能把整块视图误判成不可点。
                style={{ position: 'relative', zIndex: 1, cursor: hasRecordAction ? 'pointer' : undefined }}
                data-stop-action-propagation={hasRecordAction ? true : undefined}
                onClick={handleRecordClick}
            />
            {/* 为了不想在：未选中视图时，点击第一个以外的卡片，会触发卡片的点击事件及动作。目标是先选中视图，才能有后续的交互 */}
            {showMask && <div style={{ position: 'absolute', zIndex: 2, inset: 0 }} />}
        </>
    )
})

function nodeOrChildrenSelected(selectedNodes: undefined | NodeIdWithScope[], node: FlowLayoutNode): boolean {
    const isSelected = selectedNodes?.some(item => item.id === node.id && item.scope === node.scope)
    if (isSelected) {
        return true
    }

    if (node.children) {
        return node.children.some(item => nodeOrChildrenSelected(selectedNodes, item))
    }

    return false
}
